"use client";

import { get, set } from "idb-keyval";

const PREFIX = "hiive:overlay-transfer:";
const MAX_AGE_MS = 60 * 60 * 1000;

type TransferRecord<T> = {
  createdAt: number;
  payload: T;
};

const memory = new Map<string, TransferRecord<unknown>>();

const storageKey = (path: string) => `${PREFIX}${path}`;

const isFresh = <T>(
  record: TransferRecord<T> | null | undefined,
): record is TransferRecord<T> =>
  !!record && Date.now() - record.createdAt <= MAX_AGE_MS;

/** Synchronous hand-off used by the soft-navigation that opens an overlay. */
export function peekOverlayTransfer<T>(path: string): T | null {
  const record = memory.get(path) as TransferRecord<T> | undefined;
  if (!isFresh(record)) {
    memory.delete(path);
    return null;
  }
  return record.payload;
}

/**
 * Persist launch data long enough for back/forward and refresh. IndexedDB's
 * structured clone supports File objects, which the Story recap composer uses.
 */
export function saveOverlayTransfer<T>(path: string, payload: T) {
  const record: TransferRecord<T> = { createdAt: Date.now(), payload };
  memory.set(path, record);
  void set(storageKey(path), record).catch(() => {
    // Memory still covers the current soft navigation. Storage can be denied
    // in private browsing without making the overlay itself unusable.
  });
}

export async function loadOverlayTransfer<T>(path: string): Promise<T | null> {
  const immediate = peekOverlayTransfer<T>(path);
  if (immediate) return immediate;

  try {
    const record = (await get(storageKey(path))) as
      | TransferRecord<T>
      | undefined;
    if (!isFresh(record)) return null;
    memory.set(path, record);
    return record.payload;
  } catch {
    return null;
  }
}
